import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
  Input,
  Badge,
} from "reactstrap";
import { Btn, P, H5 } from "../../../../AbstractElements";
import apiRequestHelper from "../../../../utils/apiRequestHelper";
import { toast } from "react-toastify";
import Breadcrumbs from "../../../../CommonElements/Breadcrumbs/Breadcrumbs";

interface CTA {
  id: number;
  imageUrl?: string;
  heading?: string;
  paragraph?: string;
  buttonText: string;
  buttonHref: string;
  isActive: boolean;
}

interface BlogCTA {
  id: number;
  blogId: number;
  ctaId: number;
  sectionIndex: number;
  cta: CTA;
}

interface TocItem {
  heading: string;
}

const BlogCTAAssignPage = () => {
  const { id } = useParams(); // blog id
  const blogId = Number(id);
  const navigate = useNavigate();

  const [availableCTAs, setAvailableCTAs] = useState<CTA[]>([]);
  const [assigned, setAssigned] = useState<BlogCTA[]>([]);
  const [toc, setToc] = useState<TocItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!blogId || Number.isNaN(blogId)) return;
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blogId]);

  const fetchAll = async () => {
    try {
      setLoading(true);

      const [ctasRes, assignedRes, tocRes] = await Promise.all([
        apiRequestHelper.get("/ctas"),
        apiRequestHelper.get(`/blog-ctas/blog/${blogId}`),
        apiRequestHelper.get(`/blogs/toc/${blogId}`),
      ]);

      const activeCTAs = (ctasRes.data.data.ctas as CTA[]).filter(
        (c) => c.isActive
      );
      setAvailableCTAs(activeCTAs);

      setAssigned(assignedRes.data.data.blogCTAs as BlogCTA[]);
      setToc((tocRes.data.data.sections as TocItem[]) || []);
    } catch (err: any) {
      toast.error(
        err.response?.data?.messages?.[0]?.message || "Failed to load CTAs"
      );
    } finally {
      setLoading(false);
    }
  };

  const assignedMap = useMemo(() => {
    const m = new Map<number, BlogCTA>();
    assigned.forEach((a) => m.set(a.ctaId, a));
    return m;
  }, [assigned]);

  const isAssigned = (ctaId: number) => assignedMap.has(ctaId);

  const maxSectionIndex = Math.max(0, toc.length - 1);

  const clampSectionIndex = (val: number) => {
    if (Number.isNaN(val)) return 0;
    if (val < 0) return 0;
    if (val > maxSectionIndex) return maxSectionIndex;
    return val;
  };

  const handleAssignOne = async (cta: CTA) => {
    try {
      if (!blogId || Number.isNaN(blogId)) return;
      if (isAssigned(cta.id)) return;

      setSaving(true);

      await apiRequestHelper.post("/blog-ctas", {
        blogId,
        ctaId: cta.id,
        sectionIndex: 0,
      });

      toast.success("CTA assigned successfully!");
      await fetchAll();
    } catch (err: any) {
      toast.error(
        err.response?.data?.messages?.[0]?.message || "Failed to assign CTA"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveOne = async (ctaId: number) => {
    try {
      const row = assignedMap.get(ctaId);
      if (!row?.id) return;

      setSaving(true);

      await apiRequestHelper.del(`/blog-ctas/${row.id}`);

      toast.success("CTA removed successfully!");
      await fetchAll();
    } catch (err: any) {
      toast.error(
        err.response?.data?.messages?.[0]?.message || "Failed to remove CTA"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSectionIndexChange = async (ctaId: number, val: number) => {
    try {
      const row = assignedMap.get(ctaId);
      if (!row?.id) return;

      const safeVal = clampSectionIndex(Number(val) || 0);

      // Optional: optimistic UI
      setAssigned((prev) =>
        prev.map((x) =>
          x.ctaId === ctaId ? { ...x, sectionIndex: safeVal } : x
        )
      );

      setSaving(true);

      await apiRequestHelper.put(`/blog-ctas/${row.id}`, {
        sectionIndex: safeVal,
      });

      toast.success("CTA position updated!");
      await fetchAll();
    } catch (err: any) {
      toast.error(
        err.response?.data?.messages?.[0]?.message ||
          "Failed to update CTA position"
      );
      // rollback by refetch
      await fetchAll();
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Breadcrumbs mainTitle="Assign CTAs" parent="Blogs" />
      <Container fluid>
        {/* <Row className="mb-3">
          <Col className="d-flex justify-content-between align-items-center">
            <div className="d-flex gap-2">
              <Btn
                color="secondary"
                onClick={() => navigate("/blogs/cards")}
                disabled={saving}
              >
                Back
              </Btn>
            </div>

            <Btn
              color="primary"
              onClick={fetchAll}
              disabled={saving || loading}
            >
              Refresh
            </Btn>
          </Col>
        </Row> */}

        {loading ? (
          <Row>
            <Col className="text-center">
              <p className="mb-0">Loading...</p>
            </Col>
          </Row>
        ) : (
          <Row>
            {/* Assigned table */}
            <Col lg="8" className="mb-3">
              <Card>
                <CardBody>
                  <h5 className="mb-3">
                    Assigned CTAs{" "}
                    <Badge color="primary">{assigned.length}</Badge>
                  </h5>

                  {assigned.length === 0 ? (
                    <p className="text-muted mb-0">No CTAs assigned yet.</p>
                  ) : (
                    <div className="table-responsive">
                      <Table
                        bordered
                        hover
                        responsive
                        className="align-middle mb-0"
                      >
                        <thead>
                          <tr>
                            <th style={{ width: 70 }}>Image</th>
                            <th>CTA</th>
                            <th style={{ width: 160 }}>Section Index</th>
                            <th style={{ width: 120 }} className="text-center">
                              Remove
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {assigned.map((a) => (
                            <tr key={a.ctaId}>
                              <td>
                                {a.cta?.imageUrl ? (
                                  <img
                                    src={a.cta.imageUrl}
                                    alt="cta"
                                    style={{
                                      width: 56,
                                      height: 40,
                                      borderRadius: 6,
                                      objectFit: "cover",
                                    }}
                                  />
                                ) : (
                                  "N/A"
                                )}
                              </td>

                              <td>
                                <strong>
                                  {a.cta?.heading || "Button-only CTA"}
                                </strong>
                                <div className="text-muted f-12">
                                  {a.cta?.buttonText}
                                </div>
                              </td>

                              <td>
                                <Input
                                  type="number"
                                  min={0}
                                  max={maxSectionIndex}
                                  value={a.sectionIndex}
                                  disabled={saving || toc.length === 0}
                                  onChange={(e) =>
                                    handleSectionIndexChange(
                                      a.ctaId,
                                      Number(e.target.value)
                                    )
                                  }
                                />
                              </td>

                              <td className="text-center">
                                <Btn
                                  color="danger"
                                  size="sm"
                                  disabled={saving}
                                  onClick={() => handleRemoveOne(a.ctaId)}
                                >
                                  Remove
                                </Btn>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* Right side: TOC + Available CTAs */}
            <Col lg="4" className="mb-3">
              {/* TOC Card */}
              <Card className="mb-3">
                <CardBody>
                  <h5 className="mb-3">
                    TOC <Badge color="primary">{toc.length}</Badge>
                  </h5>

                  {toc.length === 0 ? (
                    <p className="text-muted mb-0">No headings found.</p>
                  ) : (
                    <div className="table-responsive">
                      <Table bordered responsive className="align-middle mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: 70 }}>#</th>
                            <th>Heading</th>
                          </tr>
                        </thead>
                        <tbody>
                          {toc.map((t, index) => (
                            <tr key={index}>
                              <td>{index}</td>
                              <td>{t.heading ? t.heading : "No Heading"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Available CTAs */}
            </Col>
          </Row>
        )}

        <Card>
          <CardBody>
            <h5 className="mb-3">Available CTAs</h5>

            {availableCTAs.filter((cta) => !isAssigned(cta.id)).length === 0 ? (
              <p className="text-muted mb-0">
                {availableCTAs.length === 0
                  ? "No active CTAs available. Create CTAs first from the CTAs menu."
                  : "All active CTAs are already assigned to this blog."}
              </p>
            ) : (
              <div>
                {availableCTAs
                  .filter((cta) => !isAssigned(cta.id))
                  .map((cta) => (
                    <Card key={cta.id}>
                      <CardBody>
                        <Row className="align-items-center">
                          <Col>
                            <H5>Image</H5>
                            {cta.imageUrl ? (
                              <img
                                src={cta.imageUrl}
                                alt={cta.heading}
                                style={{
                                  width: "200px",
                                  height: "100px",
                                  //   aspectRatio: "16/9",
                                  objectFit: "contain",
                                  borderRadius: "8px",
                                  marginBottom: "10px",
                                }}
                              />
                            ) : (
                              <P className="mb-0">N/A</P>
                            )}
                          </Col>

                          <Col>
                            <H5>Heading</H5>
                            {cta.heading ? (
                              <P className="mb-2">{cta.heading}</P>
                            ) : (
                              <P className="mb-2">N/A</P>
                            )}
                          </Col>

                          <Col>
                            <H5>Paragraph</H5>
                            {cta.paragraph ? (
                              <P className="text-muted small mb-3">
                                {(() => {
                                  const text = cta.paragraph ?? "";
                                  return text.length > 80
                                    ? text.substring(0, 80) + "..."
                                    : text;
                                })()}
                              </P>
                            ) : (
                              <P className="mb-0">N/A</P>
                            )}
                          </Col>

                          <Col>
                            <>
                              <H5 className="mb-3">Button Text</H5>
                              <P className="text-muted small mb-3">
                                {cta.buttonText}
                              </P>
                            </>
                          </Col>

                          <Col>
                            <Btn
                              color={
                                isAssigned(cta.id) ? "secondary" : "primary"
                              }
                              size="sm"
                              disabled={isAssigned(cta.id)}
                              onClick={() => handleAssignOne(cta)}
                            >
                              {isAssigned(cta.id)
                                ? "Already Assigned"
                                : "Assign"}
                            </Btn>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  ))}
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
    </>
  );
};

export default BlogCTAAssignPage;
